/**
 * Fetches the rich per-member detail for one duplicate group (products or
 * sales) - the data behind `DuplicateGroupCompareDialog`. One hook for both
 * kinds so the dialog stays a single shell over the two comparison views.
 *
 * `refreshKey` forces a refetch after a lifecycle mutation. A 404 is NOT an
 * error here: the group endpoints only return open-tagged members, so a
 * fully-ignored/resolved group disappears - surfaced as `groupClosed` for
 * the dialog to close gracefully instead of flashing an error.
 */
import { useEffect, useState } from 'react'
import inspectorApi, {
  DuplicateGroupDetailResponse,
  DuplicateSaleGroupDetailResponse,
} from '../services/inspector/api'

export type DuplicateGroupKind = 'product' | 'sale'

export interface UseDuplicateGroupDetailResult {
  productData: DuplicateGroupDetailResponse | null
  saleData: DuplicateSaleGroupDetailResponse | null
  loading: boolean
  error: string | null
  groupClosed: boolean
}

const isNotFound = (err: any): boolean =>
  err?.response?.status === 404 || err?.status === 404

export function useDuplicateGroupDetail(
  fpCode: string | undefined,
  groupId: string | null,
  kind: DuplicateGroupKind,
  refreshKey = 0,
): UseDuplicateGroupDetailResult {
  const [productData, setProductData] =
    useState<DuplicateGroupDetailResponse | null>(null)
  const [saleData, setSaleData] =
    useState<DuplicateSaleGroupDetailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [groupClosed, setGroupClosed] = useState(false)

  useEffect(() => {
    if (!fpCode || !groupId) {
      setProductData(null)
      setSaleData(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setGroupClosed(false)
    const fetchPromise =
      kind === 'sale'
        ? inspectorApi
            .getDuplicateSaleGroupDetail(fpCode, groupId)
            .then((res) => {
              if (!cancelled) {
                setSaleData(res)
                setProductData(null)
              }
            })
        : inspectorApi.getDuplicateGroupDetail(fpCode, groupId).then((res) => {
            if (!cancelled) {
              setProductData(res)
              setSaleData(null)
            }
          })
    fetchPromise
      .catch((err) => {
        if (cancelled) return
        if (isNotFound(err)) setGroupClosed(true)
        else setError(err?.message || 'Failed to load group detail')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fpCode, groupId, kind, refreshKey])

  return { productData, saleData, loading, error, groupClosed }
}

export default useDuplicateGroupDetail
